import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import classnames from "classnames";
import styles from "./CouponTable.scss";
import * as CouponsApi from "api/CouponsApi";
import ApprovalStatusIcon from "components/common/ApprovalStatusIcon";
import CouponOptionMenu from "components/coupons/widgets/CouponOptionMenu";
import OpenInvoicePDFLinkNewTab from "components/common/OpenInvoicePDFLinkNewTab";
import SortableDataTable from "components/common/SortableDataTable";
import logger from "utils/logger";
import { downloadFile } from "utils/request";
import toast from "utils/toast";
import * as StatusId from "constants/CouponStatusIds";
import { APPROVED } from "constants/CouponStatusIds";

export default function CouponTable({
    coupons,
    historical = false,
    resultsPerPage,
    downloadOptions,
    showOptionsMenu = true,
    showStatusIcon = true,
    onEditClick,
    onApproveClick,
    onDeclineClick,
    onRowSelectChange,
    selectedRowIds,
    canSelect = true,
    canViewClinicCouponAudit,
    canEditClinicCouponAudit,
    showInvoiceId = false,
}) {
    const couponsWithIndex = coupons.map((row, index) => ({ ...row, index }));

    const COLUMNS = [
        {
            name: "Inv. #",
            selector: "invoiceNumber",
            key: "invoiceNumber",
            sortable: true,
            format: (row) => (
                <OpenInvoicePDFLinkNewTab
                    className="text-primary"
                    showInvoiceId={showInvoiceId}
                    invoiceNumber={row.invoiceNumber}
                    extendedInvoiceNumber={row.extendedInvoiceNumber}
                    invoiceId={row.invoiceId}
                    groupOfferId={row.isGroupOffer ? row.actualGroupRedemptionId : null}
                />
            ),
        },
        {
            name: "Inv. Date",
            selector: "invoiceDate",
            key: "invoiceDate",
            sortable: true,
            format: (row) => moment(row.invoiceDate).format("MM/DD/YYYY"),
        },
        {
            name: "Last Name",
            selector: "lastName",
            key: "lastName",
            sortable: true,
        },
        {
            name: "Pet Name",
            selector: "petName",
            key: "petName",
            sortable: true,
        },
        {
            name: "Product",
            selector: "product",
            key: "product",
            sortable: true,
            format: (row) => (
                <div>
                    <span>
                        {row.product} / {row.quantity}
                    </span>
                </div>
            ),
        },
        {
            name: "Offer",
            selector: "offerName",
            key: "offerName",
            sortable: true,
        },
        {
            name: showStatusIcon ? "Status" : "",
            selector: "status",
            key: "status",
            format: (row) => getStatusAndMenu(row, { showStatusIcon, canEditClinicCouponAudit, showOptionsMenu, onEditClick, onApproveClick, onDeclineClick }),
        },
    ];

    const handleDownloadExcel = async () => {
        if (canViewClinicCouponAudit) {
            toast.success("Generating Export...");

            const res = await CouponsApi.downloadCouponHistory(downloadOptions);
            downloadFile(res, "xlsx");
        }
    };

    const getRowClassName = (row) => {
        let classNames = "";
    
        // Check for "pendingCouponsFirstInvoice"
        if (
            row.invoiceNumber === 1602 ||
            row.invoiceNumber === 337641 ||
            row.invoiceNumber === 337650 ||
            row.index === 0
        ) {
            console.log("ROW INDEX NUMBERS or FIRST ROW");
            classNames += "pendingCouponsFirstInvoice ";
        }
    
        // Check for "pendingCouponsRowRequiresValidation"
        if (
            row.actualRedemptionId === 13140976 ||
            row.actualRedemptionId === 14002698 ||
            row.actualRedemptionId === 13216538 ||
            row.actualRedemptionId === 20666004
        ) {
            console.log("TEST", row.actualRedemptionId);
            classNames += "pendingCouponsRowRequiresValidation ";
        }
    
        // Trim any extra spaces and return the class names
        return classNames.trim();
    };
    return (
        <SortableDataTable
            columns={COLUMNS}
            rawData={couponsWithIndex}
            striped
            selectable={canSelect && canEditClinicCouponAudit && (!historical || props.canUndoDeclined)}
            selectedRowIds={selectedRowIds}
            onRowSelectChange={onRowSelectChange}
            noPagination={!resultsPerPage}
            resultsPerPage={resultsPerPage}
            getRowClassName={getRowClassName}
            selectionDisabledIcon={(row) => {
                if (row.status === APPROVED) {
                    return null;
                } else {
                    return (
                        <span
                            className={classnames("margin-right-sm fa-lg", {
                                pointer: canEditClinicCouponAudit,
                            })}
                            onClick={() => (canEditClinicCouponAudit ? onEditClick(row.id) : {})}
                        >
                            <i className="fas fa-exclamation-triangle text-danger" />
                        </span>
                    );
                }
            }}
            downloadExcel={!!(historical && canViewClinicCouponAudit) ? handleDownloadExcel : null}
        />
    );
}

function getStatusAndMenu({ status, invoiceNumber, id, requiresValidation }, { showStatusIcon, canEditClinicCouponAudit, showOptionsMenu, onEditClick, onApproveClick, onDeclineClick }) {
    return (
        <div className={styles.status}>
            {showStatusIcon && <ApprovalStatusIcon status={status} />}
            {canEditClinicCouponAudit && showOptionsMenu && (
                <CouponOptionMenu
                    onEditClick={() => (canEditClinicCouponAudit && onEditClick ? onEditClick(id) : logger.log("no handler"))}
                    onApproveClick={() => (onApproveClick ? onApproveClick(id) : {})}
                    onDeclineClick={() => (onDeclineClick ? onDeclineClick(id) : {})}
                    canEdit={requiresValidation}
                    canApprove={!(requiresValidation || status === StatusId.APPROVED)}
                    canDecline={onDeclineClick && status !== StatusId.DECLINED}
                />
            )}
        </div>
    );
}

CouponTable.propTypes = {
    coupons: PropTypes.arrayOf(PropTypes.object).isRequired,
    historical: PropTypes.bool,
    resultsPerPage: PropTypes.number,
    downloadOptions: PropTypes.shape({
        period: PropTypes.string.isRequired,
        clinicId: PropTypes.string.isRequired,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),

    // Row display options
    showOptionsMenu: PropTypes.bool,
    showStatusIcon: PropTypes.bool,
    showVendorLogos: PropTypes.bool,

    // Row menu click handlers
    onEditClick: PropTypes.func,
    onShareClick: PropTypes.func,
    onApproveClick: PropTypes.func,
    onDeclineClick: PropTypes.func,

    onInvoiceClick: PropTypes.func,
    onRowSelectChange: PropTypes.func,
    selectedRowIds: PropTypes.array,

    canSelect: PropTypes.bool,
    canViewClinicCouponAudit: PropTypes.bool,
    canEditClinicCouponAudit: PropTypes.bool,
    showInvoiceId: PropTypes.bool,
};